import ForbiddenErrorResponse from "./api/responses/ForbiddenErrorResponse";
import type SentryWrappedError from "./api/responses/SentryWrappedError";
import environment from "./environment";
import { sentryErrorSchema } from "./sentryErrorSchema";
import BadRequestErrorResponse from "@app/config/api/responses/BadRequestErrorResponse";
import ExperimentConfigurationDeletionError from "@app/config/api/responses/ExperimentConfigurationDeletionErrorResponse";
import NotFoundErrorResponse from "@app/config/api/responses/NotFoundErrorResponse";
import { SDLABS_CLIENT_VERSION } from "@globals";
import { ContextualError } from "@lib/utils/ContextualError";
import type * as Sentry from "@sentry/browser";
import {
  init,
  reactRouterV7BrowserTracingIntegration,
  replayIntegration,
  setTag,
  wrapCreateBrowserRouterV7
} from "@sentry/react";
import posthog from "posthog-js";
import React from "react";
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from "react-router";
import { safeParse } from "valibot";

const EXPECTED_ERRORS = [
  ForbiddenErrorResponse,
  BadRequestErrorResponse,
  ExperimentConfigurationDeletionError,
  NotFoundErrorResponse
];

const NOISY_FETCH_ERROR_MESSAGES = [
  "Failed to fetch",
  "Load failed",
  "Network request failed",
  "NetworkError when attempting to fetch resource"
];

// The public DSN can be exposed. We must protect it doing
// https://linear.app/atinary-tech/issue/DEV-532/limit-access-to-sentry-and-posthog
export const initializeSentry = () => {
  if (!environment.ATI_PUBLIC_SENTRY_DSN) {
    return;
  }

  let replaysSessionSampleRate = 0.1;
  let replaysOnErrorSampleRate = 1.0;
  let tracesSampleRate = 0.25;

  let environmentMode: string = environment.MODE;
  switch (environment.MODE) {
    case "localdev": {
      environmentMode = "local";
      tracesSampleRate = 1.0;
      replaysSessionSampleRate = 0;
      replaysOnErrorSampleRate = 0;
      break;
    }
  }

  // Sentry gets only what we attach on purpose: the user, tags, error text and
  // ContextualError ids. Console output and browser-collected PII stay out.
  init({
    dsn: environment.ATI_PUBLIC_SENTRY_DSN,
    environment: environmentMode,
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: { request: false, response: false },
      httpBodies: [],
      urlQueryParams: false
    },
    release: `sdlabs-client@${SDLABS_CLIENT_VERSION}`,

    // Sample rates
    tracesSampleRate,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    tracePropagationTargets: ["localhost:8000", /^\//],
    integrations: [
      posthog.sentryIntegration({
        organization: environment.ATI_PUBLIC_SENTRY_ORGANIZATION,
        projectId: environment.ATI_PUBLIC_SENTRY_PROJECT_ID,
        severityAllowList: ["error", "info"],
        sendExceptionsToPostHog: false
      }),
      reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      }),
      replayIntegration()
    ],
    beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
      try {
        const originalException = hint.originalException;
        if (!originalException) {
          // Return event as-if if no original exception.
          // The original exception is our custom exception.
          return event;
        }

        // Filter out expected errors from Sentry
        if (EXPECTED_ERRORS.some(e => originalException instanceof e)) {
          return null;
        }

        // Filter out noisy browser transport errors (offline, navigation aborts, etc.).
        if (originalException instanceof Error) {
          if (originalException.name === "AbortError") {
            return null;
          }

          if (NOISY_FETCH_ERROR_MESSAGES.some(message => originalException.message.includes(message))) {
            return null;
          }
        }

        // Attach structured context from ContextualError as Sentry extra data
        if (originalException instanceof ContextualError) {
          event.extra = { ...event.extra, ...originalException.context };
          return event;
        }

        if (!event.exception || !event.exception.values || !event.exception.values?.[0]) {
          // Return event as-is in unable to access first exception.
          return event;
        }

        const result = safeParse(sentryErrorSchema, originalException);
        if (!result.success) {
          // Return event as-is if unable to parse expected schema.
          return event;
        }

        const error: SentryWrappedError = result.output;

        // Update first exception
        event.exception.values[0].type = error.sentryTitle;
        event.exception.values[0].value = error.sentryMessage;

        if (typeof window !== "undefined") {
          // Set transaction title to current url
          event.transaction = window.location.href;
        }

        return event;
      } catch {
        return event;
      }
    }
  });

  if (environment.ATI_TENANT) {
    setTag("tenant", environment.ATI_TENANT);
  }
};

export const sentryCreateBrowserRouter = wrapCreateBrowserRouterV7(createBrowserRouter);
